import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { areCustomQuotesEnabled, CUSTOM_QUOTE_SETTING_KEYS } from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { Plus, Car, Info } from 'lucide-react'
import { useState } from 'react'

import {
  VehicleCard,
  type VehicleWithPricingRows,
} from '@/components/vehicles/VehicleCard'

/**
 * Which slice of the fleet this page shows. Driven by the route
 * (/vehicles, /vehicles/active, /vehicles/inactive) rather than local state, so
 * the sidebar's Active/Inactive sub-items are real, linkable destinations.
 */
export type VehiclesFilter = 'all' | 'active' | 'inactive'

interface VehiclesPageProps {
  filter?: VehiclesFilter
}

const HEADINGS: Record<VehiclesFilter, { title: string; description: string }> = {
  all: { title: 'Vehicles', description: 'Manage your fleet of vehicles' },
  active: {
    title: 'Active Vehicles',
    description: 'Vehicles currently shown on your website',
  },
  inactive: {
    title: 'Inactive Vehicles',
    description: 'Hidden from your website until reactivated',
  },
}

export function VehiclesPage({ filter = 'all' }: VehiclesPageProps) {
  const navigate = useNavigate()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [deleteVehicleId, setDeleteVehicleId] = useState<string | null>(null)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Pricing rows come back with the vehicles (one round trip) so each card can
  // show what the vehicle costs without opening it.
  const { data: vehicles, isLoading } = useQuery({
    queryKey: ['vehicles', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('vehicles')
        .select('*, vehicle_pricing (*)')
        .eq('account_id', activeAccount.id)
        .order('display_order')
      if (error) throw error
      return data as VehicleWithPricingRows[]
    },
    enabled: !!activeAccount?.id,
  })

  // The account-level master switch. Without it, `is_quote_only` and
  // `quote_distance_miles` do nothing, so the cards must not present them as
  // active — see describeVehicleQuoteMode.
  const { data: customQuotesEnabled = false } = useQuery({
    queryKey: ['vehicles-custom-quotes-enabled', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return false
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', activeAccount.id)
        .eq('key', CUSTOM_QUOTE_SETTING_KEYS.ENABLED)
      if (error) throw error
      return areCustomQuotesEnabled(
        Object.fromEntries((data ?? []).map((s) => [s.key, s.value ?? '']))
      )
    },
    enabled: !!activeAccount?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (vehicleId: string) => {
      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', vehicleId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      toast({ title: 'Vehicle deleted' })
      setDeleteVehicleId(null)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  if (!activeAccount) {
    return null
  }

  const allVehicles = vehicles ?? []
  const activeVehicles = allVehicles.filter((v) => v.is_active)
  const inactiveVehicles = allVehicles.filter((v) => !v.is_active)

  // Active first, inactive last — the ordering the sections below rely on, and
  // what makes the unfiltered view usable for an account with retired vehicles.
  const sections =
    filter === 'active'
      ? [{ key: 'active' as const, heading: null, vehicles: activeVehicles }]
      : filter === 'inactive'
        ? [{ key: 'inactive' as const, heading: null, vehicles: inactiveVehicles }]
        : [
            {
              key: 'active' as const,
              heading: inactiveVehicles.length > 0 ? 'Active' : null,
              vehicles: activeVehicles,
            },
            {
              key: 'inactive' as const,
              heading: 'Inactive',
              vehicles: inactiveVehicles,
            },
          ]

  const visibleCount = sections.reduce((n, s) => n + s.vehicles.length, 0)

  // A vehicle configured for custom quotes while the account switch is off is
  // configuration that silently does nothing; the cards flag it individually and
  // this points at the one setting that turns them all on.
  const hasIneffectiveQuoteConfig =
    !customQuotesEnabled &&
    allVehicles.some(
      (v) => v.is_quote_only || Number(v.quote_distance_miles) > 0
    )

  const { title, description } = HEADINGS[filter]

  const renderGrid = (items: VehicleWithPricingRows[]) => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((vehicle) => (
        <VehicleCard
          key={vehicle.id}
          vehicle={vehicle}
          customQuotesEnabled={customQuotesEnabled}
          onOpen={() => navigate(`${basePath}/vehicles/${vehicle.id}`)}
          onEdit={() => navigate(`${basePath}/vehicles/${vehicle.id}/edit`)}
          onDelete={() => setDeleteVehicleId(vehicle.id)}
        />
      ))}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
        </div>
        <Button onClick={() => navigate(`${basePath}/vehicles/new`)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Vehicle
        </Button>
      </div>

      {hasIneffectiveQuoteConfig && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Some vehicles are set up for customer quote requests, but custom quotes
            are switched off for this account, so customers always see a calculated
            price. Turn them on in{' '}
            <button
              className="font-medium underline"
              onClick={() => navigate(`${basePath}/settings/booking`)}
            >
              Settings &gt; Booking
            </button>
            .
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <Skeleton className="aspect-video w-full" />
              <CardContent className="p-4">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="mt-2 h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : visibleCount > 0 ? (
        <div className="space-y-8">
          {sections
            .filter((section) => section.vehicles.length > 0)
            .map((section) => (
              <div key={section.key} className="space-y-4">
                {section.heading && (
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.heading}
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      ({section.vehicles.length})
                    </span>
                  </div>
                )}
                {renderGrid(section.vehicles)}
              </div>
            ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Car className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-semibold">
            {filter === 'inactive'
              ? 'No inactive vehicles'
              : filter === 'active'
                ? 'No active vehicles'
                : 'No vehicles yet'}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {filter === 'inactive'
              ? 'Every vehicle in your fleet is currently live on your website'
              : filter === 'active'
                ? 'Activate a vehicle to show it on your website'
                : 'Add your first vehicle to start showcasing your fleet'}
          </p>
          {filter !== 'inactive' && (
            <Button
              className="mt-4"
              onClick={() => navigate(`${basePath}/vehicles/new`)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Vehicle
            </Button>
          )}
        </div>
      )}

      <AlertDialog
        open={!!deleteVehicleId}
        onOpenChange={() => setDeleteVehicleId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this vehicle? This will also
              delete all associated features, pricing options, and add-ons. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteVehicleId && deleteMutation.mutate(deleteVehicleId)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
